import { FEATURES, FEATURE_MAP } from './featureRegistry';
import { NAV_ITEMS, BOTTOM_TAB_ITEMS, ROUTE_FEATURE_MAP } from '@/lib/nav.config';

/**
 * Standard preset definitions for exact comparison
 */
export const PRESET_DEFINITIONS = {
  'All live': {
    killSwitch: false,
    readOnlyAll: false,
    featureCheck: (feat) => feat.enabled !== false && feat.mode === 'live'
  },
  'Exhibition': {
    killSwitch: false,
    readOnlyAll: false,
    liveSet: new Set(['attendance_today', 'live_feed', 'chat', 'notifications_bell', 'simulate_punch', 'gate_display']),
    featureCheck: (feat, id, def) => {
      if (feat.enabled === false) return false;
      const shouldBeLive = def.liveSet.has(id);
      return feat.mode === (shouldBeLive ? 'live' : 'demo');
    }
  },
  'All demo / offline': {
    killSwitch: true,
    featureCheck: (feat) => feat.mode === 'demo'
  },
  'Safe (read-only everywhere)': {
    readOnlyAll: true,
    featureCheck: (feat) => feat.access === 'readonly' || true
  }
};

/**
 * Compute the effective operational state of a feature.
 * Takes into account: enabled, mode, access, global kill switch, readOnlyAll, and static demo availability.
 */
export function effectiveState(featureId, storeState) {
  const meta = FEATURE_MAP.get(featureId);
  const feat = storeState?.features?.[featureId] || {
    enabled: meta?.defaultEnabled ?? true,
    mode: meta?.defaultMode || 'live',
    access: meta?.defaultAccess || (meta?.writable ? 'readwrite' : 'readonly')
  };

  const isEnabled = feat.enabled !== false;
  const isKillSwitchActive = Boolean(storeState?.killSwitch || storeState?.offline);
  const isReadOnly = Boolean(storeState?.readOnlyAll || feat.access === 'readonly' || !meta?.writable);
  const supportsDemo = Boolean(meta?.supportsDemo);

  // If demo is chosen but not supported, mode stays live or blocked
  let resolvedMode = feat.mode || 'live';
  if (resolvedMode === 'demo' && !supportsDemo) {
    resolvedMode = 'live';
  }

  return {
    id: featureId,
    label: meta?.label || featureId,
    group: meta?.group || 'Core & Identity',
    enabled: isEnabled,
    mode: resolvedMode,
    requestedMode: feat.mode || 'live',
    access: isReadOnly ? 'readonly' : 'readwrite',
    writable: Boolean(meta?.writable),
    isReadOnly,
    isActionBlocked: !isEnabled || isKillSwitchActive || isReadOnly,
    blockReason: !isEnabled
      ? `Feature '${meta?.label || featureId}' is disabled`
      : isKillSwitchActive
      ? 'API is switched off in the Admin console'
      : isReadOnly
      ? `Read-only: ${meta?.label || featureId} is in read-only mode`
      : null,
    supportsDemo
  };
}

/**
 * Derived selector: Determine the active preset name by inspecting current store state.
 * Returns 'All live' | 'Exhibition' | 'All demo / offline' | 'Safe (read-only everywhere)' | 'Custom'
 */
export function activePreset(storeState) {
  if (!storeState || !storeState.features) return 'Custom';

  for (const [presetName, def] of Object.entries(PRESET_DEFINITIONS)) {
    // Check killSwitch & readOnlyAll if specified
    if (def.killSwitch !== undefined && storeState.killSwitch !== def.killSwitch) {
      continue;
    }
    if (def.readOnlyAll !== undefined && storeState.readOnlyAll !== def.readOnlyAll) {
      continue;
    }

    let allMatch = true;
    for (const f of FEATURES) {
      const featState = storeState.features[f.id] || {
        enabled: f.defaultEnabled ?? true,
        mode: f.defaultMode || 'live',
        access: f.defaultAccess || (f.writable ? 'readwrite' : 'readonly')
      };

      if (!def.featureCheck(featState, f.id, def)) {
        allMatch = false;
        break;
      }
    }

    if (allMatch) {
      return presetName;
    }
  }

  return 'Custom';
}

/**
 * Derived selector: Summarize counts across all features.
 * Returns { total, live, demo, readonly, readwrite, enabled, disabled }
 */
export function modeSummary(storeState) {
  const features = storeState?.features || {};
  let total = FEATURES.length;
  let live = 0;
  let demo = 0;
  let readonly = 0;
  let readwrite = 0;
  let enabled = 0;
  let disabled = 0;

  for (const f of FEATURES) {
    const feat = features[f.id] || {
      enabled: f.defaultEnabled ?? true,
      mode: f.defaultMode || 'live',
      access: f.defaultAccess || (f.writable ? 'readwrite' : 'readonly')
    };

    if (feat.enabled !== false) {
      enabled++;
    } else {
      disabled++;
    }

    if (feat.mode === 'demo') {
      demo++;
    } else {
      live++;
    }

    if (storeState?.readOnlyAll || feat.access === 'readonly' || !f.writable) {
      readonly++;
    } else {
      readwrite++;
    }
  }

  return { total, live, demo, readonly, readwrite, enabled, disabled };
}

/**
 * Derived selector: Return the visible navigation items for a given role,
 * filtering out hiddenMenus and disabled features.
 */
export function visibleMenus(role, storeState) {
  const items = NAV_ITEMS[role] || [];
  const hiddenList = storeState?.hiddenMenus?.[role] || [];
  const features = storeState?.features || {};

  return items.filter((item) => {
    // 1. Check hiddenMenus
    if (hiddenList.includes(item.to)) {
      return false;
    }
    // 2. Check feature flag
    const featId = ROUTE_FEATURE_MAP[item.to];
    if (featId && features[featId]?.enabled === false) {
      return false;
    }
    return true;
  });
}

/**
 * Derived selector: Return visible bottom tab items for a role
 */
export function visibleBottomTabs(role, storeState) {
  const items = BOTTOM_TAB_ITEMS[role] || [];
  const hiddenList = storeState?.hiddenMenus?.[role] || [];
  const features = storeState?.features || {};

  return items.filter((item) => {
    if (item.isMore) return true;
    if (hiddenList.includes(item.to)) return false;
    const featId = ROUTE_FEATURE_MAP[item.to];
    if (featId && features[featId]?.enabled === false) return false;
    return true;
  });
}

/**
 * Derived selector: Identify any logged requests that did not map to a recognized feature.
 */
export function unmappedRequests(requests = []) {
  return requests.filter((r) => !r.featureId || r.featureId === 'core' || !FEATURE_MAP.has(r.featureId));
}
